import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion } from "framer-motion";
import { useSettings, resolveImageUrl } from "../lib/SettingsContext";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Video Editing", href: "#video-editing" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar({ onQuoteClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const logoSrc = resolveImageUrl(settings?.logo_url) || "/logo.png";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "glass border-b border-gray-100 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between py-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img
            src={logoSrc}
            alt="PPrints and Services"
            className="h-12 w-12 rounded-full object-cover bg-white ring-1 ring-gray-200 group-hover:scale-105 transition"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-display font-extrabold text-lg text-brand-ink tracking-tight">
              <span className="text-brand-ink">P</span><span className="text-brand-green">P</span><span className="text-brand-ink">rints</span>
              <span className="text-brand-blue"> and </span>
              <span className="text-brand-green">S</span><span className="text-brand-ink">ervices</span>
            </div>
            <div className="text-[11px] tracking-[0.1em] text-gray-500 font-semibold">
              We Serve You The Best
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className="text-sm font-semibold text-gray-700 hover:text-brand-blue transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            data-testid="navbar-quote-btn"
            onClick={onQuoteClick}
            className="btn-primary text-sm py-2.5 px-6"
          >
            Get a Quote
          </button>
        </div>

        <button
          data-testid="navbar-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg border border-gray-200"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="lg:hidden absolute top-full inset-x-0 bg-white border-t border-gray-100 shadow-lg"
        >
          <div className="container-x py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-3 px-3 rounded-lg font-semibold text-gray-700 hover:bg-brand-mist hover:text-brand-blue transition"
              >
                {n.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                onQuoteClick?.();
              }}
              className="btn-primary mt-3"
            >
              Get a Quote
            </button>
          </div>
        </motion.div>
      )}
    </header>
  );
}
